import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Box, Callout, Collapse, Column, InlineSelect, InlineSelectDatePicker, MenuItem, Row, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { serviceModel } from '~/models';
import { validateStartBillingAt } from '~/models/service/logic';
import { useCallouts } from './hooks/useCallouts';
import { useMenuItems } from './hooks/useMenuItems';
import { useStartBillingDates } from './hooks/useStartBillingDates';
import { applyNewSelection, getStartBillingSelection } from './InlineStartBillingControl.utils';
export var InlineStartBillingMenuItem = /*#__PURE__*/ function(InlineStartBillingMenuItem) {
    InlineStartBillingMenuItem["NextBillingDay"] = "NextBillingDay";
    InlineStartBillingMenuItem["SpecificDate"] = "SpecificDate";
    InlineStartBillingMenuItem["AcceptanceDate"] = "AcceptanceDate";
    InlineStartBillingMenuItem["Today"] = "Today";
    InlineStartBillingMenuItem["ManualInitiation"] = "ManualInitiation";
    return InlineStartBillingMenuItem;
}({});
export const GetInlineStartBillingControlTID = (parentTestId)=>getTestIds(parentTestId);
export const InlineStartBillingControl = (param)=>{
    let { service, testId, disabled = false, relationshipState, onPausedChange, onBillOnAcceptanceChange, onStartBillingAtChange } = param;
    const testIds = useMemo(()=>GetInlineStartBillingControlTID(testId), [
        testId
    ]);
    const { InlineStartBillingControlTestIds } = testIds;
    const startBillingAt = serviceModel.startBillingAt(service);
    const isAmendedService = serviceModel.isFromAmendment(service);
    const relationshipEffectiveDate = relationshipState.effectiveDate;
    const isBilledUpfront = serviceModel.isBilledUpfront(service);
    const { getToday, minimumEffectiveDate, getInitialEffectiveDate } = useStartBillingDates({
        relationshipEffectiveDate,
        isAmendedService
    });
    const menuItems = useMenuItems({
        isBilledUpfront,
        relationshipEffectiveDate,
        isAmendedService,
        getToday
    });
    const selectedItem = useMemo(()=>{
        const selection = getStartBillingSelection(service);
        const hasMatchingMenuItem = menuItems.some((item)=>item.value === selection);
        if (!hasMatchingMenuItem && selection === "Today") {
            return "AcceptanceDate";
        }
        return selection;
    }, [
        service,
        menuItems
    ]);
    const callouts = useCallouts({
        testIds,
        service,
        relationshipEffectiveDate,
        selectedItem,
        isAmendedService,
        disabled
    });
    const datePickerError = useMemo(()=>{
        if (selectedItem !== "SpecificDate" || disabled) {
            return undefined;
        }
        const error = validateStartBillingAt(service, {
            relationshipEffectiveDate,
            now: getToday()
        });
        return error === null || error === void 0 ? void 0 : error[0];
    }, [
        service,
        relationshipEffectiveDate,
        getToday,
        selectedItem,
        disabled
    ]);
    const selectionChangeHandlers = useMemo(()=>({
            onPausedChange,
            onBillOnAcceptanceChange,
            onStartBillingAtChange
        }), [
        onPausedChange,
        onBillOnAcceptanceChange,
        onStartBillingAtChange
    ]);
    const handleSelectionChange = useCallback((newItem)=>{
        applyNewSelection({
            serviceId: service.id,
            newItem,
            previousItem: selectedItem,
            handlers: selectionChangeHandlers,
            getInitialEffectiveDate
        });
    }, [
        service.id,
        selectedItem,
        selectionChangeHandlers,
        getInitialEffectiveDate
    ]);
    const getSelectedDisplayText = useCallback(()=>{
        const item = menuItems.find((m)=>m.value === selectedItem);
        if (!item) {
            return 'starting on the next scheduled billing date';
        }
        return item.selectedText;
    }, [
        selectedItem,
        menuItems
    ]);
    return /*#__PURE__*/ _jsxs(Box, {
        style: {
            display: 'contents'
        },
        testId: InlineStartBillingControlTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                alignItems: "start",
                gap: 4,
                children: [
                    /*#__PURE__*/ _jsx(InlineSelect, {
                        skin: "primary",
                        disabled: disabled,
                        value: selectedItem,
                        testId: InlineStartBillingControlTestIds.DropdownMenu.Root(),
                        onChange: (e)=>{
                            const newValue = e.target.value;
                            handleSelectionChange(newValue);
                        },
                        menuMaxWidth: 344,
                        menuMaxHeight: undefined,
                        SelectProps: {
                            renderValue: ()=>getSelectedDisplayText()
                        },
                        children: menuItems.map((item)=>{
                            var _item_tooltip;
                            return /*#__PURE__*/ _jsxs(MenuItem, {
                                testId: InlineStartBillingControlTestIds.DropdownMenu.MenuItem(item.value),
                                value: item.value,
                                disabled: item.disabled,
                                variant: "multiline",
                                tooltipProps: {
                                    title: (_item_tooltip = item.tooltip) !== null && _item_tooltip !== void 0 ? _item_tooltip : ''
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        weight: "normal",
                                        children: item.menuText
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph3",
                                        children: item.description
                                    })
                                ]
                            }, item.value);
                        })
                    }),
                    selectedItem === "SpecificDate" && /*#__PURE__*/ _jsx(InlineSelectDatePicker, {
                        disabled: disabled,
                        size: "small",
                        format: "MMM d, yyyy",
                        minDate: minimumEffectiveDate,
                        testId: InlineStartBillingControlTestIds.DatePicker.Root(),
                        value: startBillingAt,
                        error: !!datePickerError,
                        helperText: datePickerError,
                        onChange: (date)=>{
                            onStartBillingAtChange === null || onStartBillingAtChange === void 0 ? void 0 : onStartBillingAtChange(service.id, date || null);
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Collapse, {
                in: callouts.length > 0,
                unmountOnExit: true,
                mountOnEnter: true,
                timeout: {
                    enter: 350,
                    exit: 350
                },
                children: /*#__PURE__*/ _jsx(Column, {
                    gap: 8,
                    style: {
                        marginTop: 12
                    },
                    children: callouts.map((callout)=>/*#__PURE__*/ _jsx(Callout, {
                            intent: "warning",
                            testId: callout.dataTestId,
                            children: callout.text
                        }, callout.dataTestId))
                })
            })
        ]
    });
};
